import { useCurrentUser } from '@/features/users/users.hooks';
import {
  buildList,
  compareListEvents,
  BuiltList,
  List,
  ListEvent,
  mergeSimilarModifications,
  SessionUser,
} from '@certhon/models';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { toast } from 'sonner';
import { useChecklist, usePostListEventsMutation } from '../lists.hooks';
import { listQueries } from '../lists.queries';
import * as store from './ListStore';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ListMode = 'view' | 'fill-in' | 'edit';

// ---------------------------------------------------------------------------
// Context value
// ---------------------------------------------------------------------------

interface ListContextValue {
  /** Server data (with events). */
  list: List;
  /** Primary list + dependency lists */
  allLists: List[];
  /** Rebuilt tree (server + pending events). */
  builtList: BuiltList;
  /** Whether this list is a template (werk_id is null). */
  isTemplate: boolean;
  /** Current mode: view (read-only), fill-in (auto-persist), edit (pending queue). */
  mode: ListMode;
  /**
   * Set the active mode. Pass `null` to clear the override and restore the
   * default mode for this entity (fill-in for open checklists, view for
   * templates / closed checklists). Hard-coding 'view' on edit-mode exit
   * leaves checklists stuck with disabled controls.
   */
  setMode: (mode: ListMode | null) => void;
  /** Current session user. */
  user: SessionUser;
  /** Locally queued events not yet persisted (edit mode only). */
  pendingEvents: ListEvent[];
  /** Append a single event. In fill-in mode, posts immediately. In edit mode, queues. */
  addEvent: (event: ListEvent) => void;
  /** Discard pending structural changes (edit mode only). */
  revert: () => void;
  /** Merge, post, and clear pending events (edit mode only). */
  commit: () => Promise<void>;
  /** True when there are unsaved structural changes (edit mode only). */
  hasChanges: boolean;
  /**
   * Inject pre-fetched dependency lists into the per-list store so a
   * subsequent EXPAND_PROTO event has its proto available without a
   * roundtrip. Keyed off the current `list.id` internally.
   */
  injectLists: (lists: List[]) => void;
  /** Clear any previously injected lists for this list. */
  clearInjectedLists: () => void;
}

const ListCtx = createContext<ListContextValue | null>(null);

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useListContext(): ListContextValue {
  const ctx = useContext(ListCtx);
  if (!ctx) {
    throw new Error('useListContext must be used within a <ListProvider>');
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Default mode
// ---------------------------------------------------------------------------

function getDefaultMode(_builtList: BuiltList, isTemplate: boolean): ListMode {
  if (isTemplate) return 'view';
  if (_builtList.status === 'closed') return 'view';
  return 'fill-in';
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ListProviderProps {
  listId: string;
  children: ReactNode;
  /** Rendered until the list and user are loaded. */
  fallback?: ReactNode;
}

export function ListProvider({
  listId,
  children,
  fallback = null,
}: ListProviderProps) {
  const user = useCurrentUser();
  const { data: list, allLists } = useChecklist(listId, { suspense: false });

  // Shared store: pendingEvents + modeOverride live at module level so that
  // sibling panels with the same listId share the same state.
  const storeState = useSyncExternalStore(
    useCallback((cb) => store.subscribe(listId, cb), [listId]),
    useCallback(() => store.getSnapshot(listId), [listId]),
  );

  const { pendingEvents, modeOverride, injectedLists } = storeState;

  // Ref-count the store entry so it gets cleaned up when all providers unmount.
  useEffect(() => {
    store.addRef(listId);
    return () => store.releaseRef(listId);
  }, [listId]);

  const postEventsMutation = usePostListEventsMutation();
  const queryClient = useQueryClient();

  // Rebuild the tree whenever the server data or pending events change.
  // allLists includes the primary list + dependency lists (fetched in one call).
  // Injected lists are merged on top so client-side EXPAND_PROTO events that
  // reference a template not yet in the list's server-side `dependencies`
  // column can still be resolved by the reducer.
  const builtList = useMemo(() => {
    if (!list) return null;
    // Merge by id (server wins on duplicates — optimistic copies get pruned
    // by the effect below once their server twin arrives) and re-sort by
    // `created`, then `recnum`. Concurrent fill-in mutations can complete out of order, so
    // a later-resolving server event may already sit in list.events while
    // an earlier client event is still pending; naively appending pending
    // after list.events would violate the reducer's monotonic-time check.
    const byId = new Map<string, ListEvent>();
    for (const e of list.events) byId.set(e.id, e);
    for (const e of pendingEvents) if (!byId.has(e.id)) byId.set(e.id, e);
    const allEvents = Array.from(byId.values()).sort(compareListEvents);
    const effectiveList = { ...list, events: allEvents };
    // De-duplicate by id: server-fetched deps win when both present.
    const seen = new Set<string>([effectiveList.id]);
    const effectiveAllLists: List[] = [effectiveList];
    for (const l of allLists.slice(1)) {
      if (seen.has(l.id)) continue;
      seen.add(l.id);
      effectiveAllLists.push(l);
    }
    for (const l of Object.values(injectedLists)) {
      if (seen.has(l.id)) continue;
      seen.add(l.id);
      effectiveAllLists.push(l);
    }
    return buildList(effectiveList, effectiveAllLists);
  }, [list, pendingEvents, allLists, injectedLists]);

  const isTemplate = list?.werk_id == null;

  const mode =
    modeOverride ??
    (builtList ? getDefaultMode(builtList, isTemplate) : 'view');

  const setMode = useCallback(
    (newMode: ListMode | null) => {
      store.setModeOverride(listId, newMode);
    },
    [listId],
  );

  // In fill-in mode, events are persisted immediately but applied
  // optimistically to pendingEvents so the UI reflects the change without
  // waiting for the round trip. The pending entry survives until the
  // refetched server data contains the same event id (see prune effect
  // below). Removing on per-event mutate completion races with subsequent
  // toggles: a still-in-flight later event would briefly disappear from
  // builtList when an earlier event's removal lands before its own refetch.
  // In edit mode, events are queued until commit.
  // In view mode, addEvent is a no-op.
  const addEvent = useCallback(
    async (event: ListEvent) => {
      if (mode === 'view') return;

      store.setPendingEvents(listId, (prev) => [...prev, event]);

      if (mode !== 'fill-in') return; // edit mode keeps it queued for commit

      try {
        await postEventsMutation.mutateAsync([event]);
      } catch (error) {
        store.setPendingEvents(listId, (prev) =>
          prev.filter((e) => e.id !== event.id),
        );
        toast.error('Failed to save', {
          description: error instanceof Error ? error.message : String(error),
        });
      }
    },
    [mode, postEventsMutation, listId],
  );

  // Prune optimistic pending events once the refetched server data carries
  // them. Matches by event id so out-of-order completions don't drop later
  // events that are still in flight.
  useEffect(() => {
    if (!list || pendingEvents.length === 0) return;
    const persistedIds = new Set(list.events.map((e) => e.id));
    if (!pendingEvents.some((e) => persistedIds.has(e.id))) return;
    store.setPendingEvents(listId, (prev) =>
      prev.filter((e) => !persistedIds.has(e.id)),
    );
  }, [list, pendingEvents, listId]);

  const revert = useCallback(() => {
    store.setPendingEvents(listId, []);
    // Drop any client-injected templates queued for pending EXPAND_PROTO
    // events; without the events that referenced them they're dead weight.
    store.clearInjectedLists(listId);
  }, [listId]);

  const commit = useCallback(async () => {
    if (pendingEvents.length === 0) return;

    const merged = mergeSimilarModifications(pendingEvents);

    try {
      await postEventsMutation.mutateAsync(merged);
      store.setPendingEvents(listId, []);
      // Server-side `dependencies` column will now reflect the new EXPAND_PROTO
      // references, so the next fetch will repopulate `allLists` authoritatively.
      store.clearInjectedLists(listId);
      queryClient.invalidateQueries({
        queryKey: listQueries.keys.detail(listId),
      });
    } catch (error) {
      toast.error('Failed to save changes', {
        description: error instanceof Error ? error.message : String(error),
      });
    }
  }, [pendingEvents, postEventsMutation, queryClient, listId]);

  // Surface server-fetched deps + client-injected lists to consumers
  // (e.g. FieldOriginSection, InheritancePanel) so protoChain entries
  // pointing at a freshly injected template can still be resolved before
  // the next server fetch.
  const effectiveAllLists = useMemo(() => {
    if (!list) return allLists;
    const seen = new Set<string>(allLists.map((l) => l.id));
    const merged = [...allLists];
    for (const l of Object.values(injectedLists)) {
      if (seen.has(l.id)) continue;
      seen.add(l.id);
      merged.push(l);
    }
    return merged;
  }, [list, allLists, injectedLists]);

  // Don't render children until we have server data + user
  if (!list || !builtList || !user) return <>{fallback}</>;

  const value: ListContextValue = {
    list,
    allLists: effectiveAllLists,
    builtList,
    isTemplate,
    mode,
    setMode,
    user,
    pendingEvents,
    addEvent,
    revert,
    commit,
    hasChanges: mode === 'edit' && pendingEvents.length > 0,
    injectLists: (lists) => store.addInjectedLists(listId, lists),
    clearInjectedLists: () => store.clearInjectedLists(listId),
  };

  return <ListCtx.Provider value={value}>{children}</ListCtx.Provider>;
}
